import { useEffect, useState } from 'react'
import type { IAssetAssetRO } from 'api'
import { fetchAssetById } from '@/lib/fetchAsset'

type FormPayload = {
  firstMiddleName?: string
  lastName?: string
  gender?: string
  birthDate?: string
  nationality?: string
  preferredLanguage?: string
  preferredLanguageOther?: string
  messengers?: { type: string; typeOther?: string; contact: string }[]
  procedureAreas?: string[]
  procedureOther?: string
  improvementDescription?: string
  budget?: string
  plannedTiming?: string
  previousSurgery?: string
  medicalHistory?: string
  currentMedications?: string
  flightBooked?: string
  preferredConsultationDate?: string
  preferredSurgeryDate?: string
  photoAssetIds?: number[]
}

function PayloadRow({
  label,
  value,
}: {
  label: string
  value?: string | null
}) {
  if (!value?.trim()) {
    return null
  }

  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 border-slate-100 border-b py-2 last:border-b-0">
      <span className="font-medium text-gray-600">{label}</span>
      <span className="whitespace-pre-wrap text-gray-900">{value}</span>
    </div>
  )
}

function formatGender(value?: string) {
  if (value === 'MALE') return '남성'
  if (value === 'FEMALE') return '여성'
  return value
}

function formatYesNo(value?: string) {
  if (value === 'YES') return '예'
  if (value === 'NO') return '아니오'
  return value
}

function formatMessengers(
  messengers?: FormPayload['messengers'],
) {
  if (!messengers?.length) {
    return undefined
  }

  return messengers
    .map((m) => {
      const label =
        m.type === '기타' ? m.typeOther?.trim() || '기타' : m.type
      return `${label}: ${m.contact}`
    })
    .join('\n')
}

function formatProcedureAreas(payload: FormPayload) {
  const areas = [...(payload.procedureAreas ?? [])]
  if (payload.procedureOther?.trim()) {
    areas.push(`기타: ${payload.procedureOther.trim()}`)
  }
  return areas.length ? areas.join(', ') : undefined
}

function PhotoGrid({ ids }: { ids: number[] }) {
  const [photos, setPhotos] = useState<IAssetAssetRO[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!ids.length) {
      setPhotos([])
      setLoading(false)
      return
    }

    setLoading(true)
    Promise.all(ids.map((id) => fetchAssetById(id)))
      .then(setPhotos)
      .catch(() => setPhotos([]))
      .finally(() => setLoading(false))
  }, [ids])

  if (!ids.length) {
    return <p className="text-gray-500 text-sm">첨부 사진 없음</p>
  }

  if (loading) {
    return <p className="text-gray-500 text-sm">사진 불러오는 중...</p>
  }

  if (!photos.length) {
    return <p className="text-gray-500 text-sm">사진을 불러오지 못했습니다.</p>
  }

  return (
    <div className="flex flex-wrap gap-8">
      {photos.map((photo) => (
        <a
          key={photo.id}
          href={photo.path}
          target="_blank"
          rel="noreferrer"
          className="block overflow-hidden rounded border border-slate-200"
        >
          <img
            src={photo.thumbnailPath || photo.path}
            alt={`consultation-photo-${photo.id}`}
            className="h-24 w-24 object-cover"
          />
        </a>
      ))}
    </div>
  )
}

export function ConsultationFormPayloadPanel({
  payload,
}: {
  payload: FormPayload
}) {
  const photoAssetIds = payload.photoAssetIds ?? []

  return (
    <div className="space-y-4">
      <div className="rounded border border-slate-200 bg-white p-3">
        <PayloadRow
          label="이름"
          value={`${payload.firstMiddleName ?? ''} ${payload.lastName ?? ''}`.trim()}
        />
        <PayloadRow label="성별" value={formatGender(payload.gender)} />
        <PayloadRow label="생년월일" value={payload.birthDate} />
        <PayloadRow label="국적/거주국" value={payload.nationality} />
        <PayloadRow
          label="희망 상담 언어"
          value={
            payload.preferredLanguageOther?.trim()
              ? `${payload.preferredLanguage} (${payload.preferredLanguageOther})`
              : payload.preferredLanguage
          }
        />
        <PayloadRow label="연락처" value={formatMessengers(payload.messengers)} />
        <PayloadRow
          label="시술 분야"
          value={formatProcedureAreas(payload)}
        />
        <PayloadRow
          label="개선 희망"
          value={payload.improvementDescription}
        />
        <PayloadRow label="예산" value={payload.budget} />
        <PayloadRow label="진행 시기" value={payload.plannedTiming} />
        <PayloadRow label="이전 수술" value={payload.previousSurgery} />
        <PayloadRow label="병력/알레르기" value={payload.medicalHistory} />
        <PayloadRow label="복용 약" value={payload.currentMedications} />
        <PayloadRow
          label="항공권 예약"
          value={formatYesNo(payload.flightBooked)}
        />
        <PayloadRow
          label="희망 상담일"
          value={payload.preferredConsultationDate}
        />
        <PayloadRow
          label="희망 시술일"
          value={payload.preferredSurgeryDate}
        />
      </div>

      <div className="space-y-2">
        <p className="font-medium text-gray-700 text-sm">
          첨부 사진 ({photoAssetIds.length}장)
        </p>
        <PhotoGrid ids={photoAssetIds} />
      </div>
    </div>
  )
}
